import '../css/SaveSnapshotModal.less';
import * as S from './WBModal.styles';

import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import {Button, Checkbox} from 'semantic-ui-react';

import ModifiedDropdown from './elements/ModifiedDropdown';
import * as ViewHooks from '../state/views/hooks';
import {
  appendSectionsToReport,
  editReportPanelGroups,
  pushReport,
} from '../state/reports/hooks';
import * as Report from '../util/report';
import {Config, emptyReportRunSetSelectAll} from '../util/section';
import * as RunSetViewTypes from '../state/views/runSet/types';
import {useWholeArray} from '../state/views/hooks';
import {RunQueryContext} from '../state/runs/context';
import * as PanelSettingsTypes from '../state/views/panelSettings/types';
import * as CustomRunColorsViewTypes from '../state/views/customRunColors/types';
import {useUserProjectsQuery} from '../generated/graphql';
import {useApolloClient} from '../state/hooks';
import {
  PanelBankContext,
  PanelBankUpdaterContext,
} from '../state/panelbank/context';
import DraftWarningModal from './DraftWarningModal';
import {LayedOutPanel, LayoutParameters} from '../util/panels';
import {useViewer} from '../state/viewer/hooks';
import {PanelBankSectionConfigWithVisiblePanels} from '../util/panelbank';
import ReportSectionPicker, {NEW_SECTION_IDX} from './ReportSectionPicker';
import {
  getFullWidthPanelLayout,
  getNewGridItemLayout,
} from '../util/panelbankGrid';
import {CreateReportOpts, PROJECT_UPDATE_INITIAL_BLOCKS} from '../util/report';
import makeComp from '../util/profiler';
import produce from 'immer';
import * as ColorUtil from '../util/colors';
import {
  getAllMetrics,
  getLegendOverrideKey,
  getMetricIdentifiersFromExpressions,
} from '../util/plotHelpers';
import {parseExpressions} from './PanelExpressionOptions';
import {expressionToString} from '../util/expr';
import {PANEL_TYPE as runsLinePlotType} from './PanelRunsLinePlot';
import {PanelBankSectionConfig} from '../state/views/panelBankSectionConfig/types';
import WBIcon from './elements/WBIcon';
import * as Section from '../util/section';
import moment from 'moment';
import * as Filters from '../util/filters';
import {isPanelGrid} from './Slate/plugins/panel-grids';

const NEW_REPORT_ID = 'new';

const DRAFT_WARNING_MAIN_TEXT =
  'You have unsaved edits to this report. Do you want to discard unsaved edits and import new panels?';
const DRAFT_WARNING_DISCARD_TEXT = 'Import panels';
const DRAFT_APPEND_MAIN_TEXT =
  'You have unsaved edits to this report. Do you want to resume or discard these unsaved edits?';
const DRAFT_APPEND_DISCARD_TEXT = 'Discard';

type TemplateType = 'snapshot' | 'dashboard' | 'update' | 'blank';

type ModalPage = 'template' | 'import' | 'export';

export interface DropdownOption {
  text: string;
  value: string | number;
}

interface SelectedSection extends PanelBankSectionConfigWithVisiblePanels {
  visibleOnly: boolean;
}

interface SectionWithPanelCount
  extends PanelBankSectionConfigWithVisiblePanels {
  panelCount: number;
}

interface SaveSnapshotModalContentProps {
  panelSettingsRef?: PanelSettingsTypes.Ref;
  runSetRefs: RunSetViewTypes.Ref[];
  customRunColorsRef: CustomRunColorsViewTypes.Ref;

  panelIsActive?: (
    section: PanelBankSectionConfig,
    panel: LayedOutPanel
  ) => boolean;
  // This is an expensive hook to call! It can inspect the entire
  // panelbank config (huge if there are many metrics), or report.
  // So only call it when we need to (when the user has opened the
  // export modal).
  useExportableSections(): PanelBankSectionConfigWithVisiblePanels[];
}

const SaveSnapshotModalContent: FC<SaveSnapshotModalContentProps> = makeComp(
  ({
    panelSettingsRef,
    runSetRefs,
    customRunColorsRef,
    useExportableSections,
    panelIsActive,
  }) => {
    const viewer = useViewer();
    const userProjectsQuery = useUserProjectsQuery({
      variables: {userName: viewer?.username ?? '', includeReports: true},
      skip: viewer == null,
    });
    const {
      entityName,
      projectName,
      mergeFilters,
      runSetName,
      report,
      runId,
    } = useContext(RunQueryContext);
    if (entityName == null || projectName == null) {
      throw new Error('missing RunQueryContext info');
    }
    const {exportingPanelRefID, exportingSectionRefID} = useContext(
      PanelBankContext
    );
    const {
      setExportingUnknownID,
      setExportingPanelRefID,
      setExportingSectionRefID,
    } = useContext(PanelBankUpdaterContext);
    const runSets = useWholeArray(runSetRefs);
    const sectionsWithVisiblePanels = useExportableSections();
    const panelSettings = ViewHooks.useWholeMaybe(panelSettingsRef);
    const customRunColors = ViewHooks.useWhole(customRunColorsRef);

    // project, report, section, and panel selection
    const [selectedProjectID, setSelectedProjectID] = useState('');
    const [selectedReportID, setSelectedReportID] = useState(NEW_REPORT_ID);
    const [selectedSectionIdx, setSelectedSectionIdx] = useState(
      NEW_SECTION_IDX
    );
    const [reportSearchQuery, setReportSearchQuery] = useState('');
    const [draftWarningModalOpen, setDraftWarningModalOpen] = useState(false);
    const [selectedSections, setSelectedSections] = useState<SelectedSection[]>(
      []
    );

    const isSinglePanelExport =
      exportingPanelRefID != null && selectedSections.length === 1;

    // set if exporting to existing report
    const [toExistingReport, setToExistingReport] = useState<boolean>(false);
    const exportToExistingReport = useCallback(
      () => setToExistingReport(true),
      []
    );
    const resetToExistingReport = useCallback(
      () => setToExistingReport(false),
      []
    );

    // modal navigation
    const [modalPage, setModalPage] = useState<ModalPage>('template');
    const goToTemplatePage = useCallback(() => setModalPage('template'), []);
    const goToImportPage = useCallback(() => setModalPage('import'), []);
    const goToExportPage = useCallback(() => setModalPage('export'), []);

    // report template selection
    const [reportTemplate, setReportTemplate] = useState<TemplateType>('blank');

    const selectSection = useCallback(i => setSelectedSectionIdx(i), []);
    useEffect(() => {
      setSelectedSectionIdx(NEW_SECTION_IDX);
    }, [selectedReportID]);

    const {
      projectOptions,
      reportOptionsByProjectID,
      projectInfoByID,
      sectionsWithPanelCount,
    } = useSaveSnapshotOptions({
      sectionsWithVisiblePanels,
      userProjectsQuery,
      selectedSections,
      report,
      panelIsActive,
    });

    const {
      onChangeProject,
      onReportSearchQueryChange,
      onChangeReport,
      onCloseModal,
      onCloseDraftWarning,
    } = useSaveSnapshotCallbacks({
      setExportingUnknownID,
      setSelectedProjectID,
      setSelectedReportID,
      setReportSearchQuery,
      setDraftWarningModalOpen,
      setExportingPanelRefID,
      setExportingSectionRefID,
    });

    useSaveSnapshotEffects({
      userProjectsQuery,
      entityName,
      projectName,
      sectionsWithVisiblePanels,
      exportingSectionRefID,
      exportingPanelRefID,
      setSelectedSections,
      selectedProjectID,
      setSelectedProjectID,
      setSelectedReportID,
    });

    const addingToReportSection =
      isSinglePanelExport && selectedSectionIdx !== NEW_SECTION_IDX;

    const [creatingReport, setCreatingReport] = useState(false);
    const client = useApolloClient();

    interface CreateReportParams {
      reportID: string;
      template: TemplateType;
      dstEntityName: string;
      dstProjectName: string;
    }

    const createReport = useCallback(
      async ({
        reportID,
        template,
        dstEntityName,
        dstProjectName,
      }: CreateReportParams) => {
        const runSetsWithEmptyDefault =
          runSets.length > 0 ? runSets : [emptyReportRunSetSelectAll()];
        const runSetsWithProject = runSetsWithEmptyDefault.map(rs => ({
          ...rs,
          project: rs.project ?? {entityName, name: projectName},
        }));

        const reportSections: Section.Config[] = [];
        let exportedPanels: LayedOutPanel[] = [];

        selectedSections.forEach(section => {
          let totalPanels = section.panels;
          if (panelIsActive != null) {
            totalPanels = totalPanels.filter(panel =>
              panelIsActive(section, panel)
            );
          }
          exportedPanels = (section.visibleOnly
            ? section.visiblePanels
            : totalPanels) as LayedOutPanel[];
          if (runId != null) {
            exportedPanels = normalizeRunPagePanelExport(runId, exportedPanels);
          }
          const reportSection = Report.createReportSection(
            runSetsWithProject,
            customRunColors,
            panelSettings,
            section,
            exportedPanels
          );
          if (reportSection != null) {
            reportSections.push(reportSection);
          }
        });

        const createReportOpts: CreateReportOpts = {
          mergeFilters,
          runSetName,
        };

        const setMergeFilters = (f: Filters.Filter) => {
          if (createReportOpts.mergeFilters != null) {
            createReportOpts.mergeFilters = Filters.And([
              createReportOpts.mergeFilters,
              f,
            ]);
          } else {
            createReportOpts.mergeFilters = f;
          }
        };

        let reportName;
        let initialBlocks;
        let redirectQS;
        const currentTime = moment();
        const existingRunsFilter: Filters.Filter = {
          key: {section: 'run', name: 'createdAt'},
          op: '<=',
          value: currentTime.toISOString(),
          disabled: false,
        };
        switch (template) {
          case 'snapshot':
            reportName = 'Snapshot ' + currentTime.format('MMM D YYYY, h:ma');
            setMergeFilters(existingRunsFilter);
            redirectQS = {
              flasher: '',
              template: 'snapshot',
            };
            break;
          case 'dashboard':
            reportName = 'Project Dashboard';
            redirectQS = {
              flasher: '',
              template: 'dashboard',
            };
            break;
          case 'update':
            reportName = currentTime.format('MMM D') + ' Project Update';
            initialBlocks = PROJECT_UPDATE_INITIAL_BLOCKS;
            setMergeFilters(existingRunsFilter);
            redirectQS = {
              flasher: '',
              template: 'update',
            };
            break;
        }

        const reportConfig =
          reportSections.length > 0
            ? Report.fromSections(
                reportSections,
                createReportOpts,
                initialBlocks
              )
            : template === 'blank'
            ? Report.getEmptyReportConfig()
            : Report.fromRunSet(runSetsWithProject[0], createReportOpts);

        setCreatingReport(true);
        if (reportID === NEW_REPORT_ID) {
          if (isSinglePanelExport) {
            const panelGrids = reportConfig.blocks.filter(isPanelGrid);
            for (const pg of panelGrids) {
              const config = pg.metadata.panelBankSectionConfig;
              config.panels.forEach(
                p => (p.layout = getFullWidthPanelLayout())
              );
            }
          }
          await pushReport({
            client,
            entityName: dstEntityName,
            projectName: dstProjectName,
            config: reportConfig,
            reportName,
            opts: {
              newTab: true,
              redirectQS,
            },
          });
        } else {
          if (addingToReportSection) {
            await editReportPanelGroups({
              client,
              entityName: dstEntityName,
              projectName: dstProjectName,
              reportID,
              opts: {newTab: true},
              editBlocks: blocks => {
                const panelGrids = blocks.filter(isPanelGrid);
                if (selectedSectionIdx >= panelGrids.length) {
                  blocks.splice(
                    -1,
                    0,
                    ...reportConfig.blocks.filter(isPanelGrid)
                  );
                  return;
                }
                const config =
                  panelGrids[selectedSectionIdx].metadata
                    .panelBankSectionConfig;
                // ensure panel layouts
                const panelLayouts: LayoutParameters[] = [];
                for (const p of config.panels) {
                  p.layout = p.layout ?? getNewGridItemLayout(panelLayouts);
                  panelLayouts.push(p.layout);
                }
                config.panels = [
                  ...config.panels,
                  {
                    ...exportedPanels[0],
                    layout: getNewGridItemLayout(
                      config.panels.map(p => p.layout)
                    ),
                  },
                ];
              },
            });
          } else {
            await appendSectionsToReport({
              client,
              entityName: dstEntityName,
              projectName: dstProjectName,
              reportID,
              newBlocks: reportConfig.blocks.filter(isPanelGrid),
              opts: {newTab: true},
            });
          }
        }

        onCloseModal();
      },
      [
        isSinglePanelExport,
        client,
        selectedSectionIdx,
        runSets,
        panelSettings,
        customRunColors,
        selectedSections,
        mergeFilters,
        runSetName,
        onCloseModal,
        runId,
        addingToReportSection,
        panelIsActive,
        entityName,
        projectName,
      ]
    );

    const createReportOrCheckDraft = useCallback(() => {
      const projectInfo = projectInfoByID[selectedProjectID];
      if (projectInfo == null) {
        return;
      }
      const {entityName: dstEntityName, name: dstProjectName} = projectInfo;
      selectedReportID === NEW_REPORT_ID
        ? createReport({
            reportID: selectedReportID,
            template: reportTemplate,
            dstEntityName,
            dstProjectName,
          })
        : setDraftWarningModalOpen(true);
    }, [
      createReport,
      selectedProjectID,
      selectedReportID,
      projectInfoByID,
      reportTemplate,
    ]);

    const createNewReport = useCallback(
      ({
        template,
        dstEntityName,
        dstProjectName,
      }: Omit<CreateReportParams, 'reportID'>) => {
        createReport({
          reportID: NEW_REPORT_ID,
          template,
          dstEntityName,
          dstProjectName,
        });
      },
      [createReport]
    );

    const renderResumeDraftButton = (
      draftID: string,
      resuming: boolean,
      discarding: boolean,
      setResuming: (r: boolean) => void
    ) => {
      if (addingToReportSection) {
        return (
          <Button size="tiny" onClick={onCloseDraftWarning}>
            Go back
          </Button>
        );
      } else {
        return (
          <Button
            size="tiny"
            loading={resuming}
            disabled={resuming || discarding}
            onClick={() => {
              const projectInfo = projectInfoByID[selectedProjectID];
              if (projectInfo == null) {
                return;
              }
              const {
                entityName: dstEntityName,
                name: dstProjectName,
              } = projectInfo;
              setResuming(true);
              createReport({
                reportID: draftID,
                template: reportTemplate,
                dstEntityName,
                dstProjectName,
              });
            }}>
            Resume
          </Button>
        );
      }
    };

    // template selection callbacks
    const setTemplateSnapshot = useCallback(() => {
      const template = 'snapshot';
      if (isSinglePanelExport) {
        createNewReport({
          template,
          dstEntityName: entityName,
          dstProjectName: projectName,
        });
      } else {
        setReportTemplate(template);
        goToImportPage();
      }
    }, [
      isSinglePanelExport,
      entityName,
      projectName,
      goToImportPage,
      createNewReport,
    ]);

    const setTemplateDashboard = useCallback(() => {
      const template = 'dashboard';
      if (isSinglePanelExport) {
        createNewReport({
          template,
          dstEntityName: entityName,
          dstProjectName: projectName,
        });
      } else {
        setReportTemplate(template);
        goToImportPage();
      }
    }, [
      isSinglePanelExport,
      entityName,
      projectName,
      goToImportPage,
      createNewReport,
    ]);

    const setTemplateUpdate = useCallback(() => {
      const template = 'update';
      if (isSinglePanelExport) {
        createNewReport({
          template,
          dstEntityName: entityName,
          dstProjectName: projectName,
        });
      } else {
        setReportTemplate(template);
        goToImportPage();
      }
    }, [
      isSinglePanelExport,
      entityName,
      projectName,
      goToImportPage,
      createNewReport,
    ]);

    const setTemplateBlank = useCallback(() => {
      const template = 'blank';
      if (isSinglePanelExport) {
        createNewReport({
          template,
          dstEntityName: entityName,
          dstProjectName: projectName,
        });
      } else {
        setReportTemplate(template);
        goToImportPage();
      }
    }, [
      isSinglePanelExport,
      entityName,
      projectName,
      goToImportPage,
      createNewReport,
    ]);

    const templateOptions = [
      {
        icon: 'camera',
        label: 'Snapshot',
        info: 'Save your work in progress',
        onClick: () => {
          setTemplateSnapshot();
        },
      },
      {
        icon: 'dashboard',
        label: 'Dashboard',
        info: 'Persistent link to live charts',
        onClick: () => {
          setTemplateDashboard();
        },
      },
      {
        icon: 'collaborate',
        label: 'Update',
        info: 'Describe findings, collaborate',
        onClick: () => {
          setTemplateUpdate();
        },
      },
      {
        icon: 'blank-page',
        label: 'Blank',
        info: 'Take quick notes, organize charts',
        onClick: () => {
          setTemplateBlank();
        },
      },
    ];

    // switch modal title based on flow
    let modalHeader = 'Create Report';
    if (exportingSectionRefID != null) {
      modalHeader = 'Save section to report';
    } else if (exportingPanelRefID != null) {
      modalHeader = 'Add panel to report';
    }

    let currentModal: ReactNode;
    switch (modalPage) {
      case 'template':
        currentModal = (
          <>
            <S.WBModalHeader>{modalHeader}</S.WBModalHeader>
            <S.WBModalContentNoActions>
              <p>
                Get a persistent link to share charts, capture insights, and
                collaborate on projects.
              </p>
              <div className="template-msg">Pick a template</div>
              {templateOptions.map(opt => (
                <div
                  className="template-option"
                  key={opt.label}
                  onClick={opt.onClick}>
                  <WBIcon name={opt.icon} />
                  <span className="template-label">{opt.label}</span>
                  <span className="template-info">{opt.info}</span>
                </div>
              ))}
              <div
                className="existing-report-button"
                onClick={() => {
                  exportToExistingReport();
                  isSinglePanelExport ? goToExportPage() : goToImportPage();
                }}>
                Add to an existing report →
              </div>
            </S.WBModalContentNoActions>
          </>
        );
        break;
      case 'import':
        currentModal = (
          <>
            <S.WBModalHeader>Import Charts</S.WBModalHeader>
            <S.WBModalContent>
              {sectionsWithVisiblePanels.length > 0 ? (
                <>
                  {/* Using inline styles just here.
                  This file has a mix of styled components and css,
                  which makes styling a little trickier. */}
                  <p style={{marginBottom: '0px'}}>
                    Pick charts from this project to add to the report.
                  </p>
                  <div className="section-options-container">
                    {sectionsWithPanelCount.map(section => {
                      const sectionExists = (s: SelectedSection) =>
                        s.ref.id === section.ref.id;
                      const currentSection = selectedSections.find(
                        sectionExists
                      );

                      return (
                        <div className="section-options" key={section.ref.id}>
                          <div className="save-snapshot-modal__checkbox">
                            <Checkbox
                              label={section.name}
                              checked={currentSection != null}
                              onChange={(e, {checked}) => {
                                if (checked == null) {
                                  return;
                                }
                                if (checked) {
                                  setSelectedSections(prev =>
                                    produce(prev, (draft: typeof prev) => {
                                      draft.push({
                                        ...section,
                                        visibleOnly: true,
                                      });
                                    })
                                  );
                                } else {
                                  setSelectedSections(prev =>
                                    prev.filter(s => !sectionExists(s))
                                  );
                                }
                              }}
                            />
                            <span className="section-panel-count">
                              ({section.panelCount} panel
                              {section.panelCount === 1 ? '' : 's'})
                            </span>
                          </div>
                          {currentSection != null && (
                            <div className="section-toggle-container">
                              <Checkbox
                                toggle
                                className="section-toggle"
                                checked={
                                  currentSection != null
                                    ? currentSection.visibleOnly
                                    : false
                                }
                                onChange={(e, {checked}) => {
                                  if (checked == null) {
                                    return;
                                  }
                                  setSelectedSections(prev =>
                                    produce(prev, draft => {
                                      const s = draft.find(sectionExists);
                                      if (s != null) {
                                        s.visibleOnly = checked;
                                      }
                                    })
                                  );
                                }}
                              />
                              <span className="section-toggle-label">
                                Only import the first page of the section
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p>
                  You have no panels on this page, so this will create an empty
                  report with your chosen template.
                </p>
              )}
            </S.WBModalContent>
            <S.WBModalActions>
              <S.WBModalButton
                content="Back"
                onClick={() => {
                  goToTemplatePage();
                  resetToExistingReport();
                }}
              />
              <S.WBModalButton
                primary
                loading={creatingReport}
                disabled={creatingReport}
                onClick={
                  toExistingReport
                    ? goToExportPage
                    : () => {
                        const projectInfo = projectInfoByID[selectedProjectID];
                        if (projectInfo == null) {
                          return;
                        }
                        const {
                          entityName: dstEntityName,
                          name: dstProjectName,
                        } = projectInfo;
                        createNewReport({
                          template: reportTemplate,
                          dstEntityName,
                          dstProjectName,
                        });
                      }
                }
                content={toExistingReport ? 'Add to report' : 'Create report'}
              />
            </S.WBModalActions>
          </>
        );
        break;
      case 'export':
        currentModal = (
          <>
            <S.WBModalHeader>Add To Report</S.WBModalHeader>
            <S.WBModalContent>
              <div
                className={classNames('destination-section', {
                  'no-margin': report != null,
                })}>
                <p>Pick a destination report.</p>
                <div className="dest-row">
                  <div className="dest-row-dest">Project</div>
                  <ModifiedDropdown
                    selection
                    search
                    className="save-snapshot-modal__select"
                    options={projectOptions}
                    value={selectedProjectID}
                    onChange={onChangeProject}
                  />
                </div>
                <div className="dest-row">
                  <div className="dest-row-dest">Report</div>
                  <ModifiedDropdown
                    selection
                    className="save-snapshot-modal__select"
                    options={reportOptionsByProjectID[selectedProjectID] ?? []}
                    value={selectedReportID}
                    onChange={onChangeReport}
                    search
                    searchQuery={reportSearchQuery}
                    onSearchChange={onReportSearchQueryChange}
                  />
                </div>
                {selectedReportID !== NEW_REPORT_ID && (
                  <div className="dest-row">
                    <div className="dest-row-dest">Section</div>
                    <ReportSectionPicker
                      reportID={selectedReportID}
                      selectedSectionIdx={selectedSectionIdx}
                      className="save-snapshot-modal__select"
                      onSelectSection={selectSection}
                    />
                  </div>
                )}
              </div>
            </S.WBModalContent>
            <S.WBModalActions>
              <Button
                onClick={
                  isSinglePanelExport ? goToTemplatePage : goToImportPage
                }>
                Back
              </Button>
              <Button
                primary
                loading={creatingReport}
                disabled={creatingReport}
                onClick={createReportOrCheckDraft}
                content="Add to report"
              />
            </S.WBModalActions>
          </>
        );
        break;
    }

    return (
      <>
        <S.WBModal
          className="save-snapshot-modal"
          open={true}
          onClose={onCloseModal}>
          {currentModal}
        </S.WBModal>
        {draftWarningModalOpen && selectedReportID !== NEW_REPORT_ID && (
          <DraftWarningModal
            parentViewID={selectedReportID}
            mainText={
              addingToReportSection
                ? DRAFT_WARNING_MAIN_TEXT
                : DRAFT_APPEND_MAIN_TEXT
            }
            discardText={
              addingToReportSection
                ? DRAFT_WARNING_DISCARD_TEXT
                : DRAFT_APPEND_DISCARD_TEXT
            }
            renderCancelNode={renderResumeDraftButton}
            flipButtonColors
            onNoDraftFound={() => {
              const projectInfo = projectInfoByID[selectedProjectID];
              if (projectInfo == null) {
                return;
              }
              const {
                entityName: dstEntityName,
                name: dstProjectName,
              } = projectInfo;
              createReport({
                reportID: selectedReportID,
                template: reportTemplate,
                dstEntityName,
                dstProjectName,
              });
            }}
            onDiscard={() => {
              const projectInfo = projectInfoByID[selectedProjectID];
              if (projectInfo == null) {
                return;
              }
              const {
                entityName: dstEntityName,
                name: dstProjectName,
              } = projectInfo;
              createReport({
                reportID: selectedReportID,
                template: reportTemplate,
                dstEntityName,
                dstProjectName,
              });
            }}
            onClose={onCloseDraftWarning}
          />
        )}
      </>
    );
  },
  {id: 'SaveSnapshotModalContent', memo: true}
);

interface SaveSnapshotModalProps {
  panelSettingsRef?: PanelSettingsTypes.Ref;
  runSetRefs: RunSetViewTypes.Ref[];
  customRunColorsRef: CustomRunColorsViewTypes.Ref;

  panelIsActive?: (
    section: PanelBankSectionConfig,
    panel: LayedOutPanel
  ) => boolean;
  useExportableSections(): PanelBankSectionConfigWithVisiblePanels[];
}

const SaveSnapshotModal: FC<SaveSnapshotModalProps> = makeComp(
  ({
    panelSettingsRef,
    runSetRefs,
    customRunColorsRef,
    useExportableSections,
    panelIsActive,
  }) => {
    const {entityName, projectName} = useContext(RunQueryContext);
    if (entityName == null || projectName == null) {
      throw new Error('missing RunQueryContext info');
    }
    const {
      exportingUnknownID: exporting,
      exportingPanelRefID,
      exportingSectionRefID,
    } = useContext(PanelBankContext);
    const isExporting =
      exporting || exportingPanelRefID != null || exportingSectionRefID != null;

    // Important: only render the modal if we need it! It fetches the entire
    // panelBank config, we don't want this to happen normally
    return isExporting ? (
      <SaveSnapshotModalContent
        useExportableSections={useExportableSections}
        panelSettingsRef={panelSettingsRef}
        runSetRefs={runSetRefs}
        customRunColorsRef={customRunColorsRef}
        panelIsActive={panelIsActive}
      />
    ) : (
      <></>
    );
  },
  {id: 'SaveSnapshotModal', memo: true}
);

export default SaveSnapshotModal;

type UserProjectsQuery = ReturnType<typeof useUserProjectsQuery>;

interface SaveSnapshotOptionsParams {
  sectionsWithVisiblePanels: PanelBankSectionConfigWithVisiblePanels[];
  userProjectsQuery: UserProjectsQuery;
  selectedSections: SelectedSection[];
  report?: {
    id: string;
    parentID?: string;
  };

  panelIsActive?: (
    section: PanelBankSectionConfig,
    panel: LayedOutPanel
  ) => boolean;
}

function useSaveSnapshotOptions({
  sectionsWithVisiblePanels,
  userProjectsQuery,
  selectedSections,
  report,
  panelIsActive,
}: SaveSnapshotOptionsParams) {
  const sectionsWithPanelCount: SectionWithPanelCount[] = useMemo(
    () =>
      sectionsWithVisiblePanels.map(s => {
        let totalPanels = s.panels;
        if (panelIsActive != null) {
          totalPanels = totalPanels.filter(p => panelIsActive(s, p));
        }
        const selectedSection = selectedSections.find(
          section => section.ref.id === s.ref.id
        );
        const saveVisible =
          selectedSection != null ? selectedSection.visibleOnly : false;
        return {
          ...s,
          panelCount: saveVisible ? s.visiblePanels.length : totalPanels.length,
        };
      }),
    [sectionsWithVisiblePanels, selectedSections, panelIsActive]
  );

  const projectOptions: DropdownOption[] = useMemo(() => {
    if (userProjectsQuery.data == null) {
      return [];
    }
    const teams = getTeamsFromUserProjectsQuery(userProjectsQuery);
    const opts: DropdownOption[] = [];
    for (const t of teams) {
      const teamOpts =
        t?.projects?.edges.map(e => ({
          text:
            teams.length > 1
              ? `${e.node?.entityName}/${e.node?.name}`
              : e.node?.name ?? '',
          value: e.node?.id ?? '',
        })) ?? [];
      opts.push(...teamOpts);
    }
    return opts;
  }, [userProjectsQuery]);

  const reportOptionsByProjectID: {
    [id: string]: DropdownOption[];
  } = useMemo(() => {
    if (userProjectsQuery.data == null) {
      return {};
    }
    const optionsByID: {
      [id: string]: DropdownOption[];
    } = {};
    const projects = getProjectsFromUserProjectsQuery(userProjectsQuery);
    for (const p of projects) {
      const reports = p.reports?.edges?.map(r => r.node) ?? [];
      optionsByID[p.id] = [
        {text: 'Create a new report', value: NEW_REPORT_ID},
        ...reports
          .map(r => ({
            text: r?.displayName ?? '',
            value: r?.id ?? '',
          }))
          .filter(o => {
            if (report == null) {
              return true;
            }
            return o.value !== report.parentID ?? report.id;
          }),
      ];
    }
    return optionsByID;
  }, [userProjectsQuery, report]);

  const projectInfoByID: {
    [id: string]: {
      entityName: string;
      name: string;
    };
  } = useMemo(() => {
    if (userProjectsQuery.data == null) {
      return {};
    }
    const infoByID: {
      [id: string]: {
        entityName: string;
        name: string;
      };
    } = {};
    const projects = getProjectsFromUserProjectsQuery(userProjectsQuery);
    for (const p of projects) {
      infoByID[p.id] = {entityName: p.entityName, name: p.name};
    }
    return infoByID;
  }, [userProjectsQuery]);

  return {
    projectOptions,
    reportOptionsByProjectID,
    projectInfoByID,
    sectionsWithPanelCount,
  };
}

interface SaveSnapshotEffectsParams {
  userProjectsQuery: UserProjectsQuery;
  entityName: string;
  projectName: string;
  selectedProjectID: string;
  sectionsWithVisiblePanels: PanelBankSectionConfigWithVisiblePanels[];
  exportingSectionRefID: string | null;
  exportingPanelRefID: string | null;
  setSelectedSections: React.Dispatch<React.SetStateAction<SelectedSection[]>>;
  setSelectedProjectID(s: string): void;
  setSelectedReportID(s: string): void;
}

function useSaveSnapshotEffects({
  userProjectsQuery,
  entityName,
  projectName,
  selectedProjectID,
  sectionsWithVisiblePanels,
  exportingSectionRefID,
  exportingPanelRefID,
  setSelectedSections,
  setSelectedProjectID,
  setSelectedReportID,
}: SaveSnapshotEffectsParams) {
  // Initialize selected sections/panel
  useEffect(() => {
    if (sectionsWithVisiblePanels.length < 1) {
      return;
    }

    // select first section by default (clicking Create Report button)
    let initialSection: PanelBankSectionConfigWithVisiblePanels =
      sectionsWithVisiblePanels[0];

    // if user clicks from section overflow menu, select relevant section
    if (exportingSectionRefID) {
      const currentSection = sectionsWithVisiblePanels.find(
        s => s.ref.id === exportingSectionRefID
      );
      if (currentSection) {
        initialSection = currentSection;
      }
    }

    // if user clicks from panel overflow menu, select relevant panel
    else if (exportingPanelRefID) {
      let currentSection;
      let currentPanel;
      for (const s of sectionsWithVisiblePanels) {
        for (const p of s.panels) {
          if ((p as any).ref?.id === exportingPanelRefID) {
            currentSection = s;
            currentPanel = p;
            break;
          }
        }
      }
      if (currentSection != null && currentPanel != null) {
        currentSection.panels = [currentPanel];
        currentSection.visiblePanels = [currentPanel];
        initialSection = currentSection;
      }
    }

    setSelectedSections(prev =>
      produce(prev, (draft: typeof prev) => {
        draft.push({
          ...initialSection,
          visibleOnly: true,
        });
      })
    );
  }, [
    setSelectedSections,
    sectionsWithVisiblePanels,
    exportingSectionRefID,
    exportingPanelRefID,
  ]);

  // select current project by default
  useEffect(() => {
    if (userProjectsQuery.data == null) {
      return;
    }
    const projects = getProjectsFromUserProjectsQuery(userProjectsQuery);
    if (projects.length === 0) {
      return;
    }

    // if a project on the list is already selected, don't change it against the user's will
    let projo = projects.find(p => p.id === selectedProjectID);
    // otherwise, select current project
    if (projo == null) {
      projo = projects.find(
        p => p.entityName === entityName && p.name === projectName
      );
    }
    // if current project is not on the list, default to first project
    if (projo == null) {
      projo = projects[0];
    }
    setSelectedProjectID(projo.id);
    // eslint-disable-next-line
  }, [userProjectsQuery, entityName, projectName]);

  // reset selection to create new report on project change
  useEffect(() => {
    setSelectedReportID(NEW_REPORT_ID);
  }, [selectedProjectID, setSelectedReportID]);
}

interface SaveSnapshotCallbacksParams {
  setSelectedProjectID(s: string): void;
  setSelectedReportID(s: string): void;
  setReportSearchQuery(s: string): void;
  setDraftWarningModalOpen(o: boolean): void;
  setExportingUnknownID(isExporting: boolean): void;
  setExportingPanelRefID(id: string | null): void;
  setExportingSectionRefID(id: string | null): void;
}

function useSaveSnapshotCallbacks({
  setSelectedProjectID,
  setSelectedReportID,
  setReportSearchQuery,
  setDraftWarningModalOpen,
  setExportingUnknownID,
  setExportingPanelRefID,
  setExportingSectionRefID,
}: SaveSnapshotCallbacksParams) {
  const onChangeProject = useCallback(
    (e, data) => setSelectedProjectID(data.value),
    [setSelectedProjectID]
  );

  const onReportSearchQueryChange = useCallback(
    (e, data) => setReportSearchQuery(data.searchQuery),
    [setReportSearchQuery]
  );

  const onChangeReport = useCallback(
    (e, data) => {
      setSelectedReportID(data.value);
      setReportSearchQuery('');
    },
    [setSelectedReportID, setReportSearchQuery]
  );

  const onCloseDraftWarning = useCallback(
    () => setDraftWarningModalOpen(false),
    [setDraftWarningModalOpen]
  );

  const onCloseModal = useCallback(() => {
    setExportingUnknownID(false);
    setExportingPanelRefID(null);
    setExportingSectionRefID(null);
  }, [setExportingUnknownID, setExportingPanelRefID, setExportingSectionRefID]);

  return {
    onChangeProject,
    onReportSearchQueryChange,
    onChangeReport,
    onCloseDraftWarning,
    onCloseModal,
  };
}

export function getTeamsFromUserProjectsQuery(query: UserProjectsQuery) {
  return _.compact(query.data?.user?.teams?.edges.map(e => e.node) ?? []);
}

export function getProjectsFromUserProjectsQuery(query: UserProjectsQuery) {
  const teams = getTeamsFromUserProjectsQuery(query);
  const projectsPerTeam = teams.map(
    t => t?.projects?.edges.map(e => e.node) ?? []
  );
  return _.compact(_.flatten(projectsPerTeam));
}

function normalizeRunPagePanelExport(
  runName: string,
  panels: LayedOutPanel[]
): LayedOutPanel[] {
  return produce(panels, ps => {
    for (const p of ps) {
      addRunNameToOverrides(runName, p);
      addDefaultColorsToOverride(runName, p);
    }
  });
}

const overrideKeys = [
  'overrideColors',
  'overrideMarks',
  'overrideSeriesTitles',
];

function addRunNameToOverrides(runName: string, p: LayedOutPanel): void {
  for (const overrideKey of overrideKeys) {
    const override: {[key: string]: unknown} | undefined = (p.config as any)[
      overrideKey
    ];
    if (override != null) {
      for (const k of Object.keys(override)) {
        override[`${runName}:${k}`] = override[k];
        delete override[k];
      }
    }
  }
}

function addDefaultColorsToOverride(runName: string, p: LayedOutPanel): void {
  if (p.viewType !== runsLinePlotType) {
    return;
  }
  const override = p.config.overrideColors ?? {};

  const {expressions, xExpression} = parseExpressions(
    p.config.expressions,
    p.config.xExpression
  );
  const {
    expressionMetricIdentifiers,
    xExpressionMetricIdentifiers,
  } = getMetricIdentifiersFromExpressions(expressions, xExpression);
  const metrics = getAllMetrics(
    p.config.metrics ?? [],
    expressionMetricIdentifiers,
    xExpressionMetricIdentifiers
  );

  const addToOverride = (metricName: string, i: number) => {
    const key = getLegendOverrideKey({
      uniqueId: runName,
      metricName,
    });
    if (override[key] != null) {
      return;
    }
    const color = ColorUtil.color(i);
    const transparentColor = ColorUtil.color(i, 0.1);
    override[key] = {color, transparentColor};
  };

  for (let i = 0; i < metrics.length; i++) {
    addToOverride(metrics[i], i);
  }
  if (expressions != null && expressions.length > 0) {
    for (let i = 0; i < expressions.length; i++) {
      addToOverride(expressionToString(expressions[i]), i);
    }
  }

  p.config.overrideColors = override;
}
